import { Controller } from "@hotwired/stimulus"
import { Editor } from "@tiptap/core"
import StarterKit from "@tiptap/starter-kit"
import Link from "@tiptap/extension-link"
import Placeholder from "@tiptap/extension-placeholder"

// Mounts TipTap on academy admin article forms and syncs JSON + HTML hidden fields.
export default class extends Controller {
  static targets = [
    "editor",
    "toolbar",
    "jsonInput",
    "htmlInput",
    "titleInput",
    "autoDraftStatus",
  ]

  static values = {
    autoDraftEnabled: Boolean,
    autoDraftUrl: String,
    autoDraftArticleId: Number,
    autoDraftPendingLabel: String,
    autoDraftErrorLabel: String,
  }

  connect() {
    if (this.hasTitleInputTarget) {
      this.resizeTitle()
    }

    this.editor = new Editor({
      element: this.editorTarget,
      extensions: [
        StarterKit,
        Link.configure({ openOnClick: false, autolink: true }),
        Placeholder.configure({ placeholder: "Écrire l'article…" }),
      ],
      content: this.parseInitialContent(),
      onUpdate: ({ editor }) => this.syncInputs(editor),
    })

    this.renderToolbar()
    this.syncInputs(this.editor)
    this.setupBubbleToolbar()

    this.boundSubmit = () => this.syncInputs(this.editor)
    this.boundGenerated = (event) => this.applyGeneratedContent(event.detail)
    this.stepForm = this.element.closest("form")
    if (this.stepForm) {
      // Turbo serializes the form on submit-start; sync hidden fields before that hook runs.
      this.stepForm.addEventListener("turbo:submit-start", this.boundSubmit, { capture: true })
      this.stepForm.addEventListener("submit", this.boundSubmit, { capture: true })
      this.stepForm.addEventListener("academy-article-form:content-generated", this.boundGenerated)
    }

    void this.maybeAutoDraftInitialContent()
  }

  disconnect() {
    this.teardownBubbleToolbar()
    if (this.stepForm) {
      this.stepForm.removeEventListener("turbo:submit-start", this.boundSubmit)
      this.stepForm.removeEventListener("submit", this.boundSubmit)
      this.stepForm.removeEventListener("academy-article-form:content-generated", this.boundGenerated)
    }
    this.editor?.destroy()
  }

  setupBubbleToolbar() {
    if (!this.hasToolbarTarget) return

    this.boundUpdateBubbleToolbar = () => this.updateBubbleToolbar()
    this.boundBlurBubbleToolbar = ({ event }) => {
      if (this.toolbarTarget.contains(event?.relatedTarget)) return

      this.toolbarTarget.hidden = true
    }

    this.editor.on("selectionUpdate", this.boundUpdateBubbleToolbar)
    this.editor.on("transaction", this.boundUpdateBubbleToolbar)
    this.editor.on("focus", this.boundUpdateBubbleToolbar)
    this.editor.on("blur", this.boundBlurBubbleToolbar)

    window.addEventListener("scroll", this.boundUpdateBubbleToolbar, true)
    window.addEventListener("resize", this.boundUpdateBubbleToolbar)
  }

  teardownBubbleToolbar() {
    if (!this.boundUpdateBubbleToolbar) return

    this.editor?.off("selectionUpdate", this.boundUpdateBubbleToolbar)
    this.editor?.off("transaction", this.boundUpdateBubbleToolbar)
    this.editor?.off("focus", this.boundUpdateBubbleToolbar)
    this.editor?.off("blur", this.boundBlurBubbleToolbar)
    window.removeEventListener("scroll", this.boundUpdateBubbleToolbar, true)
    window.removeEventListener("resize", this.boundUpdateBubbleToolbar)
  }

  updateBubbleToolbar() {
    if (!this.hasToolbarTarget || !this.editor) return

    const { from, to, empty } = this.editor.state.selection

    if (empty || from === to) {
      this.toolbarTarget.hidden = true
      return
    }

    // Unhide first so the toolbar has measurable dimensions for positioning.
    this.toolbarTarget.hidden = false
    this.refreshToolbarState()
    this.positionBubbleToolbar(from, to)
  }

  positionBubbleToolbar(from, to) {
    const { view } = this.editor
    const start = view.coordsAtPos(from)
    const end = view.coordsAtPos(to)
    const centerX = (Math.min(start.left, end.left) + Math.max(start.right, end.right)) / 2
    const width = this.toolbarTarget.offsetWidth || 0
    const margin = 8
    const clampedX = Math.min(
      Math.max(centerX, width / 2 + margin),
      window.innerWidth - width / 2 - margin
    )
    const top = Math.min(start.top, end.top) - margin

    this.toolbarTarget.style.left = `${clampedX}px`
    this.toolbarTarget.style.top = `${top}px`
    this.toolbarTarget.style.transform = "translate(-50%, -100%)"
  }

  applyGeneratedContent(detail) {
    if (!this.editor || !detail) return

    if (detail.title && this.hasTitleInputTarget) {
      this.titleInputTarget.value = detail.title
      this.resizeTitle()
    }
    let bodyJson = detail.body_json
    if (typeof bodyJson === "string") {
      try {
        bodyJson = JSON.parse(bodyJson)
      } catch (_error) {
        bodyJson = null
      }
    }

    if (bodyJson && typeof bodyJson === "object" && Array.isArray(bodyJson.content) && bodyJson.content.length > 0) {
      this.editor.commands.setContent(bodyJson)
    } else if (detail.body_html && detail.body_html.trim() !== "") {
      this.editor.commands.setContent(detail.body_html)
    } else {
      return
    }

    if (detail.generation_metadata) {
      this.syncGenerationMetadata(detail.generation_metadata)
    }

    this.syncInputs(this.editor)
  }

  syncGenerationMetadata(metadata) {
    const raw = typeof metadata === "string" ? metadata : JSON.stringify(metadata)
    const metaInput = this.stepForm?.querySelector('[name="academy_article[generation_metadata]"]')
    if (metaInput) metaInput.value = raw
  }

  autoDraftStorageKey() {
    return `academy-article-auto-draft-${this.autoDraftArticleIdValue}`
  }

  documentEmpty() {
    if (!this.editor) return true

    return this.extractPlainText(this.editor.getJSON()).trim().length === 0
  }

  extractPlainText(node) {
    if (!node || typeof node !== "object") return ""

    if (node.type === "text") return node.text || ""

    return (node.content || []).map((child) => this.extractPlainText(child)).join(" ")
  }

  async maybeAutoDraftInitialContent() {
    if (!this.autoDraftEnabledValue || !this.autoDraftUrlValue || !this.autoDraftArticleIdValue) return
    if (sessionStorage.getItem(this.autoDraftStorageKey()) === "done") return
    if (this.autoDraftInFlight) return
    if (!this.documentEmpty()) return

    this.autoDraftInFlight = true
    this.setAutoDraftGenerating(true)

    try {
      const formData = new FormData()
      formData.append("academy_article_id", String(this.autoDraftArticleIdValue))
      formData.append("only_if_empty", "true")

      const response = await fetch(this.autoDraftUrlValue, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": this.csrfToken(),
        },
        body: formData,
      })

      const payload = await response.json()

      if (response.ok && payload.skipped) {
        sessionStorage.setItem(this.autoDraftStorageKey(), "done")
        return
      }

      if (!response.ok) {
        const message = Array.isArray(payload.errors) ? payload.errors.join(", ") : this.autoDraftErrorLabelValue
        this.showAutoDraftStatus(message, true)
        return
      }

      this.applyGeneratedContent(payload)
      sessionStorage.setItem(this.autoDraftStorageKey(), "done")

      const warnings = Array.isArray(payload.warnings) ? payload.warnings.filter(Boolean) : []
      if (warnings.length > 0) {
        this.showAutoDraftStatus(warnings.join(" "), false)
      }
    } catch (_error) {
      this.showAutoDraftStatus(this.autoDraftErrorLabelValue, true)
    } finally {
      this.autoDraftInFlight = false
      this.setAutoDraftGenerating(false)
    }
  }

  setAutoDraftGenerating(active) {
    this.element.classList.toggle("academy-article-document--generating", active)
    this.element.toggleAttribute("aria-busy", active)

    if (this.hasTitleInputTarget) {
      this.titleInputTarget.readOnly = active
    }
    this.editor?.setEditable(!active)

    if (!this.hasAutoDraftStatusTarget) return

    if (active) {
      this.showAutoDraftStatus(this.autoDraftPendingLabelValue, false)
    } else if (this.autoDraftStatusTarget.dataset.error !== "true") {
      this.autoDraftStatusTarget.classList.add("d-none")
      this.autoDraftStatusTarget.textContent = ""
    }
  }

  showAutoDraftStatus(message, isError) {
    if (!this.hasAutoDraftStatusTarget || !message) return

    this.autoDraftStatusTarget.textContent = message
    this.autoDraftStatusTarget.dataset.error = isError ? "true" : "false"
    this.autoDraftStatusTarget.classList.toggle("text-danger", isError)
    this.autoDraftStatusTarget.classList.toggle("text-muted", !isError)
    this.autoDraftStatusTarget.classList.remove("d-none")
  }

  csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content
  }

  parseInitialContent() {
    const raw = this.hasJsonInputTarget ? this.jsonInputTarget.value : ""
    try {
      const parsed = JSON.parse(raw || "{}")
      if (parsed && typeof parsed === "object" && Object.keys(parsed).length > 0) {
        return parsed
      }
    } catch (_error) {
      // fall through
    }

    if (this.hasHtmlInputTarget && this.htmlInputTarget.value.trim() !== "") {
      return this.htmlInputTarget.value
    }

    return { type: "doc", content: [{ type: "paragraph" }] }
  }

  syncInputs(editor) {
    if (this.hasJsonInputTarget) {
      this.jsonInputTarget.value = JSON.stringify(editor.getJSON())
    }
    if (this.hasHtmlInputTarget) {
      this.htmlInputTarget.value = editor.getHTML()
    }
  }

  resizeTitle() {
    if (!this.hasTitleInputTarget) return

    const field = this.titleInputTarget
    field.style.height = "auto"
    field.style.height = `${field.scrollHeight}px`
  }

  renderToolbar() {
    if (!this.hasToolbarTarget) return

    const buttons = [
      {
        label: "Gras",
        icon: "fa-bold",
        isActive: () => this.editor.isActive("bold"),
        command: () => this.editor.chain().focus().toggleBold().run(),
      },
      {
        label: "Italique",
        icon: "fa-italic",
        isActive: () => this.editor.isActive("italic"),
        command: () => this.editor.chain().focus().toggleItalic().run(),
      },
      {
        label: "Titre 2",
        icon: "fa-heading",
        level: 2,
        isActive: () => this.editor.isActive("heading", { level: 2 }),
        command: () => this.editor.chain().focus().toggleHeading({ level: 2 }).run(),
      },
      {
        label: "Titre 3",
        icon: "fa-heading",
        level: 3,
        isActive: () => this.editor.isActive("heading", { level: 3 }),
        command: () => this.editor.chain().focus().toggleHeading({ level: 3 }).run(),
      },
      {
        label: "Liste à puces",
        icon: "fa-list-ul",
        isActive: () => this.editor.isActive("bulletList"),
        command: () => this.editor.chain().focus().toggleBulletList().run(),
      },
      {
        label: "Liste numérotée",
        icon: "fa-list-ol",
        isActive: () => this.editor.isActive("orderedList"),
        command: () => this.editor.chain().focus().toggleOrderedList().run(),
      },
      {
        label: "Lien",
        icon: "fa-link",
        isActive: () => this.editor.isActive("link"),
        command: () => this.setLink(),
      },
      {
        label: "Citation",
        icon: "fa-quote-right",
        isActive: () => this.editor.isActive("blockquote"),
        command: () => this.editor.chain().focus().toggleBlockquote().run(),
      },
    ]

    this.toolbarTarget.innerHTML = ""
    this.toolbarButtons = buttons.map(({ label, icon, level, isActive, command }) => {
      const button = document.createElement("button")
      button.type = "button"
      button.className = "btn btn-link bg-shadow btn-sm academy-tiptap-toolbar__button"
      button.title = label
      button.setAttribute("aria-label", label)
      button.innerHTML = `<i class="fa-solid ${icon}" aria-hidden="true"></i>`
      if (level) {
        button.insertAdjacentHTML("beforeend", `<sup aria-hidden="true">${level}</sup>`)
      }
      button.addEventListener("mousedown", (event) => {
        // Keep text selected when clicking a formatting control.
        event.preventDefault()
      })
      button.addEventListener("click", (event) => {
        event.preventDefault()
        command()
        this.updateBubbleToolbar()
      })
      this.toolbarTarget.appendChild(button)

      return { button, isActive }
    })
  }

  refreshToolbarState() {
    this.toolbarButtons?.forEach(({ button, isActive }) => {
      const active = isActive()
      button.classList.toggle("active", active)
      button.setAttribute("aria-pressed", active ? "true" : "false")
    })
  }

  setLink() {
    const previous = this.editor.getAttributes("link").href
    const url = window.prompt("URL du lien", previous || "https://")
    if (url === null) return

    if (url === "") {
      this.editor.chain().focus().extendMarkRange("link").unsetLink().run()
      return
    }

    this.editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run()
  }
}
